import { useState, useCallback, useRef, useEffect } from 'react';
import { getMyLots, getUserLotLists } from '../../services/userLotsService';
import { getCadProjects } from '../../services/cadService';
import { ownedCadProjectsForLists } from '../../Showroom/utils/cadCollectionsForLists';
import { normalizeLotsData } from '../utils/userLotUnits';

/** Alcance por defecto: filtrar sobre todas las parcelas públicas. */
export const PARCEL_FILTER_SCOPE_ALL = { type: 'all' };

/**
 * useParcelFilterScope
 *
 * Alcance (scope) del panel de filtros: "Todos los lotes", "Mis registros",
 * una lista catastral o una lista CAD. Las catastrales resuelven `lot_id`
 * contra `/getmylots`. Las CAD no: un `custom_parcels.id` no es un `lots.id`.
 *
 * Pide `include=filter_attributes,private_attributes`: las filas traen los
 * atributos de parcela y los de publicación/UF, para evaluar los filtros
 * contra DB (pins a zoom bajo y conteo exacto).
 *
 * Con `ownerUserId` (espacio ajeno activo) las listas del selector son las
 * del owner (`?owner_user_id=`). Al cambiar de espacio se recargan.
 *
 * @see docs/maps/parcel-filters-scope-backend-spec.md
 */
export function useParcelFilterScope({ isLoggedIn = false, ownerUserId = null } = {}) {
  const [scope, setScopeState] = useState(PARCEL_FILTER_SCOPE_ALL);
  const [scopeIds, setScopeIds] = useState([]);
  /** Filas de getmylots (lat/lng + filter_attributes) para pins y evaluación en cliente. */
  const [scopeLots, setScopeLots] = useState([]);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeError, setScopeError] = useState(null);
  const [scopeLists, setScopeLists] = useState([]);
  const [scopeCadLists, setScopeCadLists] = useState([]);
  const [scopeListsLoading, setScopeListsLoading] = useState(false);

  const idsAbortRef = useRef(null);
  const listsAbortRef = useRef(null);
  const listsLoadedRef = useRef(false);

  // Al desloguear se descarta todo el estado de scope.
  useEffect(() => {
    if (isLoggedIn) return;
    idsAbortRef.current?.abort();
    listsAbortRef.current?.abort();
    listsLoadedRef.current = false;
    setScopeState(PARCEL_FILTER_SCOPE_ALL);
    setScopeIds([]);
    setScopeLots([]);
    setScopeLists([]);
    setScopeCadLists([]);
    setScopeError(null);
    setScopeLoading(false);
    setScopeListsLoading(false);
  }, [isLoggedIn]);

  useEffect(() => () => {
    idsAbortRef.current?.abort();
    listsAbortRef.current?.abort();
  }, []);

  const spaceOwnerId = ownerUserId ? String(ownerUserId).trim() : '';
  const prevSpaceOwnerIdRef = useRef(spaceOwnerId);

  // Cambio de espacio activo: listas del selector y alcance son de ese espacio.
  useEffect(() => {
    if (prevSpaceOwnerIdRef.current === spaceOwnerId) return;
    prevSpaceOwnerIdRef.current = spaceOwnerId;
    idsAbortRef.current?.abort();
    listsAbortRef.current?.abort();
    listsLoadedRef.current = false;
    setScopeLists([]);
    setScopeCadLists([]);
    setScopeListsLoading(false);
    // Una lista / registros del espacio anterior no aplican al nuevo.
    setScopeState(PARCEL_FILTER_SCOPE_ALL);
    setScopeIds([]);
    setScopeLots([]);
    setScopeError(null);
    setScopeLoading(false);
  }, [spaceOwnerId]);

  /** Carga (una vez) las listas del usuario para el selector de alcance. */
  const ensureScopeListsLoaded = useCallback(async () => {
    if (!isLoggedIn || listsLoadedRef.current) return;
    listsLoadedRef.current = true;

    listsAbortRef.current?.abort();
    const controller = new AbortController();
    listsAbortRef.current = controller;
    setScopeListsLoading(true);
    try {
      const ownerOpts = spaceOwnerId ? { ownerUserId: spaceOwnerId } : {};
      const [listsRes, cadRes] = await Promise.all([
        getUserLotLists({ signal: controller.signal, ...ownerOpts }),
        getCadProjects({ signal: controller.signal, ...ownerOpts }),
      ]);
      if (controller.signal.aborted) return;
      setScopeLists(Array.isArray(listsRes?.data) ? listsRes.data : []);
      setScopeCadLists(ownedCadProjectsForLists(
        cadRes?.ok ? cadRes.projects : [],
        { spaceMirror: Boolean(spaceOwnerId) },
      ));
    } catch (e) {
      if (e?.name === 'AbortError') return;
      listsLoadedRef.current = false;
      console.error('useParcelFilterScope: error cargando listas para el alcance', e);
    } finally {
      if (!controller.signal.aborted) setScopeListsLoading(false);
    }
  }, [isLoggedIn, spaceOwnerId]);

  /**
   * Cambia el alcance activo y resuelve sus ids contra la DB.
   * @param {{ type: 'all' } | { type: 'myLots' } | { type: 'list', listId: string, listName?: string } | { type: 'cad', projectId?: string, slug?: string, listName?: string }} nextScope
   */
  const setScope = useCallback(async (nextScope) => {
    idsAbortRef.current?.abort();
    const next = nextScope?.type ? nextScope : PARCEL_FILTER_SCOPE_ALL;
    setScopeState(next);
    setScopeError(null);

    if (next.type === 'all' || next.type === 'cad') {
      setScopeIds([]);
      setScopeLots([]);
      setScopeLoading(false);
      return;
    }

    const controller = new AbortController();
    idsAbortRef.current = controller;
    setScopeLoading(true);
    try {
      const res = await getMyLots({
        listId: next.type === 'list' ? next.listId : undefined,
        includeFilterAttributes: true,
        includePrivateAttributes: true,
        signal: controller.signal,
        ...(spaceOwnerId ? { ownerUserId: spaceOwnerId } : {}),
      });
      if (controller.signal.aborted) return;
      const rows = normalizeLotsData(res?.data);
      const ids = [...new Set(
        rows.map((row) => row?.lot_id).filter(Boolean).map(String),
      )];
      setScopeIds(ids);
      setScopeLots(rows);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error('useParcelFilterScope: error resolviendo ids del alcance', e);
      setScopeError('No pudimos cargar los lotes del alcance');
      setScopeIds([]);
      setScopeLots([]);
    } finally {
      if (!controller.signal.aborted) setScopeLoading(false);
    }
  }, [spaceOwnerId]);

  /** Vuelve al alcance "Todos los lotes". */
  const clearScope = useCallback(() => {
    setScope(PARCEL_FILTER_SCOPE_ALL);
  }, [setScope]);

  return {
    scope,
    scopeIds,
    scopeLots,
    isScopeActive: scope.type !== 'all' && scope.type !== 'cad',
    scopeLoading,
    scopeError,
    scopeLists,
    scopeCadLists,
    scopeListsLoading,
    setScope,
    clearScope,
    ensureScopeListsLoaded,
  };
}
